import { memo, useMemo, useState } from "react";
import {
  ALL_LAYERS,
  LAYER_GROUP_LABEL,
  LAYER_LEGEND,
  type LayerGroup,
  type LayerId,
  type LegendKind,
} from "../map/presets";
import { PanelHeader } from "./PanelHeader";
import { CONNECTOR_GROUP_LABEL } from "../map/presets";
import { tierBadgeText, type ConnectorLayerEntry } from "../map/connectorLayer";
import "./connectors.css";

interface Props {
  initiallyCollapsed?: boolean;
  /** Currently enabled layers — the legend shows ONLY these. */
  enabledLayers: Set<LayerId>;
  /** Connector layers currently switched on (registry-driven). */
  connectorLayers?: readonly ConnectorLayerEntry[];
}

// Same reading order as the LayerPalette so the legend and the palette
// tell the same story top-to-bottom.
const GROUP_ORDER: LayerGroup[] = [
  "campus",
  "operations",
  "mobility",
  "incidents",
  "utility",
  "environment",
  "xray",
];

/**
 * Swatch that mirrors the layer's actual visual encoding:
 *   line  → horizontal bar in the line color
 *   fill  → translucent fill chip with the outline color
 *   point → solid dot
 *   heat  → transparent → color gradient bar
 * Colors come from ALL_LAYERS[].swatch (data, not chrome), same source the
 * LayerPalette swatches use.
 */
function LegendSwatch({ swatch, kind }: { swatch: string; kind: LegendKind }) {
  switch (kind) {
    case "line":
      return (
        <span
          className="map-legend-sw map-legend-sw--line"
          style={{ background: swatch }}
          aria-hidden
        />
      );
    case "fill":
      return (
        <span
          className="map-legend-sw map-legend-sw--fill"
          style={{ borderColor: swatch, background: `${swatch}26` }}
          aria-hidden
        />
      );
    case "heat":
      return (
        <span
          className="map-legend-sw map-legend-sw--heat"
          style={{ background: `linear-gradient(90deg, ${swatch}00, ${swatch})` }}
          aria-hidden
        />
      );
    case "point":
    default:
      return (
        <span
          className="map-legend-sw map-legend-sw--point"
          style={{ background: swatch }}
          aria-hidden
        />
      );
  }
}

/**
 * MapLegend — floating key over the map (top-right) that decodes every
 * data layer currently on. Collapses to a small LEGEND chip so it never
 * blocks the map during a presentation.
 *
 * Entries are grouped by the same LayerGroup structure the LayerPalette
 * uses, and labels come from LAYER_LEGEND (plain language for a
 * non-technical viewer — "Metropolitan Water Authority mains", not
 * "pratumwan-water").
 */
export const MapLegend = memo(function MapLegend({ enabledLayers, connectorLayers = [], initiallyCollapsed = false }: Props) {
  const [open, setOpen] = useState(!initiallyCollapsed);

  const groups = useMemo(() => {
    const byGroup = new Map<LayerGroup, typeof ALL_LAYERS>();
    for (const l of ALL_LAYERS) {
      if (!enabledLayers.has(l.id)) continue;
      const arr = byGroup.get(l.group) ?? [];
      arr.push(l);
      byGroup.set(l.group, arr);
    }
    return GROUP_ORDER
      .map((g) => ({ group: g, layers: byGroup.get(g) ?? [] }))
      .filter((x) => x.layers.length > 0);
  }, [enabledLayers]);

  const total = useMemo(
    () => groups.reduce((n, g) => n + g.layers.length, 0) + connectorLayers.length,
    [groups, connectorLayers],
  );

  if (!open) {
    return (
      <button
        type="button"
        className="map-legend-toggle mono"
        onClick={() => setOpen(true)}
        aria-expanded={false}
        aria-label={`Open map legend — ${total} layers on`}
        title="Show what every line, fill, and dot on the map means"
      >
        LEGEND · {total}
      </button>
    );
  }

  return (
    <div className="map-legend" role="complementary" aria-label="Map legend">
      <PanelHeader
        title="MAP LEGEND"
        fallbackTier="reference"
        source="map"
        actions={
          <button
            type="button"
            className="map-legend-collapse mono"
            onClick={() => setOpen(false)}
            aria-expanded={true}
            aria-label="Collapse map legend"
            title="Collapse legend"
          >
            —
          </button>
        }
      />
      {total === 0 ? (
        <p className="caption map-legend-empty">
          No data layers on — the map is showing the base map only.
        </p>
      ) : (
        groups.map(({ group, layers }) => (
          <section key={group} className="map-legend-group">
            <div className="map-legend-group-name mono">{LAYER_GROUP_LABEL[group]}</div>
            <ul className="map-legend-list">
              {layers.map((l) => {
                const meta = LAYER_LEGEND[l.id];
                return (
                  <li key={l.id} className="map-legend-item" title={l.describe}>
                    <LegendSwatch swatch={l.swatch} kind={meta?.kind ?? "point"} />
                    <span className="map-legend-label">{meta?.label ?? l.label}</span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
      {connectorLayers.length > 0 && (
        <section className="map-legend-group" data-testid="legend-connectors">
          <div className="map-legend-group-name mono">{CONNECTOR_GROUP_LABEL}</div>
          <ul className="map-legend-list">
            {connectorLayers.map((c) => (
              <li key={c.layerId} className="map-legend-item" title={c.describe}>
                <LegendSwatch swatch={c.color} kind="point" />
                <span className="map-legend-label">{c.label}</span>
                <span className={`connector-tier-badge connector-tier-badge--${c.tier}`}>{tierBadgeText(c.tier)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
});
